import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Grid,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useGetHospitalsQuery } from "../services/hospitalService";
import { useForm, Controller } from "react-hook-form";
import {
  useCreateBedRequestMutation,
  useUpdateBedRequestMutation,
} from "../services/bedRequestService";
import toast from "react-hot-toast";
import {
  X,
  User,
  Phone,
  Mail,
  Stethoscope,
  Bed,
  AlertTriangle,
  Info,
} from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Hospital } from "../types";

interface BedRequestFormData {
  patientName: string;
  patientAge: string;
  contactNumber: string;
  email: string;
  hospitalId: Hospital | null;
  hospitalName: string;
  bedType: "general" | "icu" | "ventilator" | "emergency";
  urgencyLevel: "low" | "medium" | "high" | "critical";
  medicalCondition: string;
  additionalNotes: string;
  startDate: Date | null;
  endDate: Date | null;
  doctorNotes?: string;
  status: "pending" | "approved" | "rejected" | "completed" | "paid";
}

interface BedRequestFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bedRequest?: any;
}

const BedRequestFormModal: React.FC<BedRequestFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  bedRequest,
}) => {
  const [loading, setLoading] = useState(false);
  const [hospitalSearch, setHospitalSearch] = useState("");
  const isEdit = !!bedRequest;

  const { data: hospitalsData = [] } = useGetHospitalsQuery({
    page: 1,
    limit: 10000,
  });

  const hospitals = Array.isArray(hospitalsData?.data) ? hospitalsData?.data : [];

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BedRequestFormData>({
    defaultValues: {
      patientName: "",
      patientAge: "",
      contactNumber: "",
      email: "",
      hospitalId: null,
      hospitalName: "",
      bedType: "general",
      urgencyLevel: "medium",
      medicalCondition: "",
      additionalNotes: "",
      startDate: null,
      endDate: null,
      doctorNotes: "",
      status: "pending",
    },
  });

  const watchHospitalId: Hospital | null = watch("hospitalId");
  const watchStartDate = watch("startDate");
  // Reset form when bed request data changes
  useEffect(() => {
    if (bedRequest) {
      reset({
        ...bedRequest,
        startDate: bedRequest.startDate ? new Date(bedRequest.startDate) : null,
        endDate: bedRequest.endDate ? new Date(bedRequest.endDate) : null,
      });
    } else {
      reset({
        patientName: "",
        patientAge: "",
        contactNumber: "",
        email: "",
        hospitalId: null,
        hospitalName: "",
        bedType: "general",
        urgencyLevel: "medium",
        medicalCondition: "",
        additionalNotes: "",
        startDate: null,
        endDate: null,
        doctorNotes: "",
        status: "pending",
      });
    }
  }, [bedRequest, reset]);
  console.log('bedRequest', bedRequest);
  const [updateBedRequest] = useUpdateBedRequestMutation();
  const [createBedRequest] = useCreateBedRequestMutation();

  const onSubmit = async (data: BedRequestFormData) => {
    setLoading(true);
    try {
      const requestData = {
        ...data,
        requestedAt: new Date().toISOString(),
      };

      if (isEdit && bedRequest?._id) {
        await updateBedRequest({
          _id: bedRequest._id,
          ...requestData,
        }).unwrap();
        toast.success("Bed Request updated successfully");
      } else {
        await createBedRequest(requestData).unwrap();
        toast.success("Bed Request created successfully");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving bed request:", error);
      toast.error(error?.data?.message || "Failed to save bed request");
    } finally {
      setLoading(false);
    }
  };

  const bedTypeOptions = [
    { value: "general", label: "General Bed" },
    { value: "icu", label: "ICU Bed" },
    { value: "ventilator", label: "Ventilator Bed" },
    { value: "emergency", label: "Emergency Bed" },
  ];

  const urgencyLevels = [
    { value: "low", label: "Low", color: "success" },
    { value: "medium", label: "Medium", color: "info" },
    { value: "high", label: "High", color: "warning" },
    { value: "critical", label: "Critical", color: "error" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="bed-request-form-modal"
        aria-describedby="bed-request-form-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiBackdrop-root": {
            background: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxWidth: 1000,
            maxHeight: "90vh",
            overflowY: "auto",
            p: 3,
            position: "relative",
          }}
          elevation={3}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Bed size={24} />
              {isEdit ? "Edit Bed Request" : "New Bed Request"}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Patient Information */}
              <Grid size={6}>
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <User size={18} /> Patient Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Patient Name"
                        variant="outlined"
                        {...register("patientName", {
                          required: "Patient name is required",
                        })}
                        error={!!errors.patientName}
                        helperText={errors.patientName?.message}
                        InputProps={{
                          startAdornment: (
                            <User
                              size={20}
                              style={{ marginRight: 8, color: "#666" }}
                            />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Age"
                        type="number"
                        variant="outlined"
                        {...register("patientAge", {
                          required: "Age is required",
                          min: { value: 0, message: "Age must be positive" },
                          max: {
                            value: 120,
                            message: "Please enter a valid age",
                          },
                        })}
                        error={!!errors.patientAge}
                        helperText={errors.patientAge?.message}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Contact Number"
                        variant="outlined"
                        {...register("contactNumber", {
                          required: "Contact number is required",
                          pattern: {
                            value: /^[0-9+\-\s()]+$/,
                            message: "Please enter a valid phone number",
                          },
                        })}
                        error={!!errors.contactNumber}
                        helperText={errors.contactNumber?.message}
                        InputProps={{
                          startAdornment: (
                            <Phone
                              size={20}
                              style={{ marginRight: 8, color: "#666" }}
                            />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        variant="outlined"
                        {...register("email", {
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        InputProps={{
                          startAdornment: (
                            <Mail
                              size={20}
                              style={{ marginRight: 8, color: "#666" }}
                            />
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Medical Information */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Stethoscope size={18} /> Medical Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <FormControl fullWidth error={!!errors.urgencyLevel}>
                        <InputLabel id="urgency-level-label">
                          Urgency Level
                        </InputLabel>
                        <Controller
                          name="urgencyLevel"
                          control={control}
                          defaultValue="medium"
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="urgency-level-label"
                              label="Urgency Level"
                              startAdornment={
                                <AlertTriangle
                                  size={20}
                                  style={{
                                    marginRight: 8,
                                    color:
                                      field.value === "critical"
                                        ? "#d32f2f"
                                        : field.value === "high"
                                        ? "#ed6c02"
                                        : field.value === "medium"
                                        ? "#0288d1"
                                        : "#388e3c",
                                  }}
                                />
                              }
                            >
                              {urgencyLevels.map((level) => (
                                <MenuItem key={level.value} value={level.value}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor:
                                          level.value === "critical"
                                            ? "#d32f2f"
                                            : level.value === "high"
                                            ? "#ed6c02"
                                            : level.value === "medium"
                                            ? "#0288d1"
                                            : "#388e3c",
                                      }}
                                    />
                                    {level.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.urgencyLevel && (
                          <FormHelperText>
                            {errors.urgencyLevel.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Medical Condition"
                        variant="outlined"
                        multiline
                        rows={3}
                        {...register("medicalCondition", {
                          required: "Medical condition is required",
                        })}
                        error={!!errors.medicalCondition}
                        helperText={errors.medicalCondition?.message}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Additional Notes"
                        variant="outlined"
                        multiline
                        rows={2}
                        {...register("additionalNotes")}
                        error={!!errors.additionalNotes}
                        helperText={errors.additionalNotes?.message}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={6}>
                {/* Hospital & Bed Information */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Bed size={18} /> Bed & Hospital Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <FormControl fullWidth error={!!errors.hospitalId}>
                        <Autocomplete
                          options={hospitals}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option._id === value._id
                          }
                          onChange={(_, value) => {
                            setValue("hospitalId", value || null);
                          }}
                          onInputChange={(_, value) => setHospitalSearch(value)}
                          value={
                            hospitals.find((h: any) => h._id === watchHospitalId?._id) ||
                            null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Hospital"
                              variant="outlined"
                              error={!!errors.hospitalId}
                              helperText={errors.hospitalId?.message}
                              required
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={6}>
                      <FormControl fullWidth error={!!errors.bedType}>
                        <InputLabel id="bed-type-label">Bed Type</InputLabel>
                        <Controller
                          name="bedType"
                          control={control}
                          defaultValue="general"
                          render={({ field }) => (
                            <Select
                              {...field}
                              labelId="bed-type-label"
                              label="Bed Type"
                              startAdornment={
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor:
                                      field.value === "emergency"
                                        ? "#d32f2f"
                                        : field.value === "icu"
                                        ? "#ed6c02"
                                        : field.value === "ventilator"
                                        ? "#0288d1"
                                        : "#388e3c",
                                    marginRight: 1,
                                  }}
                                />
                              }
                            >
                              {bedTypeOptions.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor:
                                          type.value === "emergency"
                                            ? "#d32f2f"
                                            : type.value === "icu"
                                            ? "#ed6c02"
                                            : type.value === "ventilator"
                                            ? "#0288d1"
                                            : "#388e3c",
                                      }}
                                    />
                                    {type.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.bedType && (
                          <FormHelperText>
                            {errors.bedType.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid size={6}>
                      <FormControl fullWidth>
                        <Controller
                          name="status"
                          control={control}
                          defaultValue="pending"
                          render={({ field }) => (
                            <TextField
                              select
                              label="Status"
                              variant="outlined"
                              {...field}
                            >
                              {[
                                { value: "pending", label: "Pending" },
                                { value: "approved", label: "Approved" },
                                { value: "rejected", label: "Rejected" },
                                { value: "completed", label: "Completed" },
                                { value: "paid", label: "Paid" },
                              ].map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={6}>
                      <Controller
                        name="startDate"
                        control={control}
                        rules={{ required: "Start date is required" }}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            label="Start Date"
                            value={value}
                            onChange={(date: any) => {
                              onChange(date);
                              // If end date is before the new start date, update it
                              if (watchStartDate && watchStartDate > date) {
                                setValue("endDate", date);
                              }
                            }}
                            minDate={new Date()}
                            // renderInput={(params: any) => (
                            //   <TextField
                            //     {...params}
                            //     fullWidth
                            //     error={!!errors.startDate}
                            //     helperText={errors.startDate?.message}
                            //     InputProps={{
                            //       ...params.InputProps,
                            //       startAdornment: (
                            //         <Calendar
                            //           size={20}
                            //           style={{ marginRight: 8, color: "#666" }}
                            //         />
                            //       ),
                            //     }}
                            //   />
                            // )}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={6}>
                      <Controller
                        name="endDate"
                        control={control}
                        rules={{
                          required: "End date is required",
                          validate: (value) =>
                            !watchStartDate ||
                            !value ||
                            value >= watchStartDate ||
                            "End date must be after start date",
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                          <DatePicker
                            {...field}
                            label="End Date"
                            value={value}
                            onChange={onChange}
                            minDate={watchStartDate || new Date()}
                            // renderInput={(params: any) => (
                            //   <TextField
                            //     {...params}
                            //     fullWidth
                            //     error={!!errors.endDate}
                            //     helperText={errors.endDate?.message}
                            //     InputProps={{
                            //       ...params.InputProps,
                            //       startAdornment: (
                            //         <Calendar
                            //           size={20}
                            //           style={{ marginRight: 8, color: "#666" }}
                            //         />
                            //       ),
                            //     }}
                            //   />
                            // )}
                          />
                        )}
                      />
                    </Grid>

                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label="Doctor's Notes"
                        variant="outlined"
                        multiline
                        rows={3}
                        {...register("doctorNotes")}
                        error={!!errors.doctorNotes}
                        helperText={errors.doctorNotes?.message}
                        InputProps={{
                          startAdornment: (
                            <Info
                              size={20}
                              style={{
                                marginRight: 8,
                                color: "#666",
                                alignSelf: "flex-start",
                                marginTop: 8,
                              }}
                            />
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {loading
                      ? "Saving..."
                      : isEdit
                      ? "Update Request"
                      : "Create Request"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Modal>
    </LocalizationProvider>
  );
};

export default BedRequestFormModal;
